import { ContextlessQueryObserverOptions, useSchemaQuery, useSchemaQueries } from '@frontend/react-query-helpers';
import { SchemaIO } from '@frontend/schema';
import { trackScheduleAPIVersionChange } from '../helpers';
import { SchemaSchedulerV3Service, serviceName } from '../service';
import { ServiceQueries, ServiceStandardQueries, StandardQueryEndpointName } from '../types';

const DEFAULT_OPTIONS: ContextlessQueryObserverOptions = {
  refetchOnWindowFocus: false,
};

export type UseSchedulerV3QueryArgs<
  EndpointName extends StandardQueryEndpointName,
  E = unknown,
  D = SchemaIO<ServiceQueries[EndpointName]>['output']
> = Omit<Parameters<typeof useSchemaQuery<ServiceStandardQueries, EndpointName, E, D>>[0], 'service' | 'serviceName'>;
/**
 * A wrapper around useSchemaQueries that uses the SchedulerV3 service and serviceName.
 * This is intended to only be used internally in the `queries` directory of the library and not exported.
 */
export const useSchedulerV3Queries = <
  EndpointName extends StandardQueryEndpointName,
  E = unknown,
  D = SchemaIO<ServiceQueries[EndpointName]>['output']
>(
  argsArr: UseSchedulerV3QueryArgs<EndpointName, E, D>[]
) =>
  useSchemaQueries<ServiceStandardQueries, EndpointName, E, D>(
    argsArr.map(({ options, ...args }) => ({
      service: SchemaSchedulerV3Service,
      serviceName,
      ...args,
      options: {
        ...DEFAULT_OPTIONS,
        ...options,
        onSettled: (data, error) => {
          trackScheduleAPIVersionChange('v3', { endpoint: args.endpointName, type: 'multiple' });
          if (options?.onSettled) {
            options.onSettled(data, error);
          }
        },
      },
    }))
  );
